import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import MarketingShell from "./MarketingShell";
import { Plus, Trash, Play, Pause, Envelope, Clock, GitBranch, PencilSimple, Eye, PaperPlaneRight, ShieldCheck, X, Check } from "@phosphor-icons/react";
import EmailBlockBuilder, {
  emptyBlocks, extractBlocks, renderBlocksToHtml, wrapBlocksInHtml, blocksFromRawHtml,
} from "./EmailBlockBuilder";
import TestSequenceButton from "./TestSequenceButton";
import EngagementPanel from "./EngagementPanel";
import { WorkflowTabNav, WORKFLOW_TABS, BounceTrendChart, ClicksTab, EnrollmentsTab, VariantWinnerBanner, AdaptiveCapStatus, CapPauseBanner, CapOverrideHistory, RepeatIncidentBanner, FollowUpTab, ManualSendTab } from "./WorkflowTabs";
import { formatLocal, formatLocalDate, userTz } from "@/lib/timezone";
import { safeSrcDoc } from "@/lib/safeHtml";

const STATUS = {
  draft: { bg: "#F5F5F5", fg: "#525252", label: "Draft" },
  active: { bg: "#F0FDF4", fg: "#16A34A", label: "Active" },
  paused: { bg: "#FEF3C7", fg: "#92400E", label: "Paused" },
};

const STEP_ICONS = { send_email: Envelope, delay: Clock, if_else: GitBranch, update_property: PencilSimple };


/**
 * Send-window policy banner with the operator-local "next send" forecast.
 *
 * The Pulse send-window is defined in the RECIPIENT's local timezone
 * (Mon-Thu 09:30-12:00, Fri 09:30-11:00) — but the operator sitting
 * in front of the workflow detail page wants to know WHEN, in THEIR
 * own timezone, the queue will actually start firing. This banner
 * answers that question.
 *
 * Top row: status + "Next send: <Date> at <Time> in <TZ>" formatted
 *          with the user's profile/browser tz.
 * Body:    the static policy text (recipient-local).
 * Tail:    distribution buckets ("1,797 sends scheduled for Friday,
 *          another 67 for Wed May 13") so a long drain is visible.
 */
function SendWindowBanner({ forecast, workflow }) {
  const status = (workflow?.status || forecast?.workflow_status || "").toLowerCase();
  const earliest = forecast?.earliest_next_action_at;
  const buckets = forecast?.buckets || [];
  const tz = userTz();
  // Pretty version of the next-send instant — "Friday, May 08 at 06:30 AM PDT".
  const niceNextSend = (() => {
    if (!earliest) return null;
    try {
      const raw = earliest.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(earliest)
        ? earliest : earliest + "Z";
      const d = new Date(raw);
      if (isNaN(d.getTime())) return null;
      const dayPart = new Intl.DateTimeFormat(undefined, {
        weekday: "long", month: "short", day: "2-digit", timeZone: tz,
      }).format(d);
      const timePart = new Intl.DateTimeFormat(undefined, {
        hour: "2-digit", minute: "2-digit", hour12: true,
        timeZone: tz, timeZoneName: "short",
      }).format(d);
      return `${dayPart} at ${timePart}`;
    } catch { return formatLocal(earliest); }
  })();

  // STATUS pill — paused / active / draft
  const statusBg =
    status === "paused" ? "bg-[#FEF3C7] border-[#F59E0B] text-[#92400E]" :
    status === "active" ? "bg-[#DCFCE7] border-[#16A34A] text-[#15803D]" :
    "bg-[#F5F5F5] border-[#A3A3A3] text-[#525252]";

  // Outer container colouring follows status — paused = amber, active = green
  const wrapBg =
    status === "paused" ? "bg-[#FFFBEB] border-[#F59E0B]" :
    "bg-[#F0FDF4] border-[#16A34A]";

  return (
    <div
      className={`border ${wrapBg} px-4 py-3 mb-4`}
      data-testid="send-window-policy"
    >
      {/* Row 1 — status + next send (operator-local) */}
      <div className="flex items-start gap-3 flex-wrap">
        <span className="text-base leading-none mt-0.5">⏰</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <strong className="text-[12px] uppercase tracking-wider text-[#15803D]">
              Send window
            </strong>
            <span
              className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${statusBg}`}
              data-testid="send-window-status-pill"
            >
              {status === "paused" ? "Paused" : status === "active" ? "Active" : status || "—"}
            </span>
            {forecast && (
              <span className="text-[11px] text-[#525252]" data-testid="send-window-queue-size">
                · {(forecast.active_enrolments || 0).toLocaleString()} contacts queued
              </span>
            )}
          </div>

          {earliest ? (
            <div className="text-[12px] text-[#0A0A0A] mb-1.5" data-testid="send-window-next-send">
              <strong>Next send:</strong>{" "}
              <span className="font-mono">
                {niceNextSend}
              </span>
              <span className="text-[#525252] text-[11px]">
                {" "}(your local time · {tz})
              </span>
              {status === "paused" && (
                <span className="ml-2 text-[#92400E] text-[11px]">
                  · workflow is currently paused — won&apos;t fire until you click Resume
                </span>
              )}
            </div>
          ) : (
            <div className="text-[12px] text-[#737373] mb-1.5" data-testid="send-window-empty">
              No active enrolments — nothing scheduled to send.
            </div>
          )}

          {/* Static policy text */}
          <div className="text-[11px] text-[#525252] leading-relaxed">
            All automated emails respect <strong>recipient-local</strong> windows:{" "}
            <strong>Mon-Thu 09:30-12:00</strong> · <strong>Fri 09:30-11:00</strong> ·
            weekends + US federal holidays closed. Anything queued outside the window
            auto-defers to the next business day&apos;s 09:30.
          </div>

          {/* Distribution buckets */}
          {buckets.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="send-window-buckets">
              {buckets.slice(0, 6).map((b) => {
                const friendly = formatLocalDate(b.date + "T13:00:00Z");
                return (
                  <span
                    key={b.date}
                    className="text-[10px] font-mono bg-white border border-[#E5E5E5] px-2 py-0.5"
                    title={`UTC date ${b.date}`}
                  >
                    {friendly} · {b.count.toLocaleString()}
                  </span>
                );
              })}
              {buckets.length > 6 && (
                <span className="text-[10px] text-[#737373] py-0.5">
                  +{buckets.length - 6} more days
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function WorkflowsListPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "" });

  const reload = async () => {
    setLoading(true);
    try { setRows((await api.get("/marketing/workflows")).data); }
    catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const doCreate = async () => {
    if (!form.name.trim()) return;
    try {
      const r = await api.post("/marketing/workflows", {
        name: form.name, trigger_type: "manual",
        steps: [{ type: "send_email", subject: "Welcome {{first_name|default:there}}",
                  body_html: "<p>Welcome!</p>" }],
      });
      toast.success("Workflow created");
      setCreateOpen(false); setForm({ name: "" });
      nav(`/marketing/workflows/${r.data.id}`);
    } catch { toast.error("Create failed"); }
  };

  const pauseResume = async (w, e) => {
    e?.stopPropagation();
    const next = w.status === "paused" ? "active" : "paused";
    try {
      await api.put(`/marketing/workflows/${w.id}`, { status: next });
      toast.success(next === "paused" ? "Paused" : "Resumed");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  const remove = async (w, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete the "${w.name}" workflow? All its enrollments will be removed. This cannot be undone.`)) return;
    try {
      await api.delete(`/marketing/workflows/${w.id}`);
      toast.success("Deleted");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const fmt = (s) => s ? formatLocalDate(s) : "—";

  return (
    <MarketingShell>
      {/* Global adaptive-cap banner — visible when the rolling 7d bounce
          rate puts us in amber (throttled) or red (paused) band. Compact
          single-line form because the list page has its own density
          demands; full detail is on each workflow's Analytics tab. */}
      <CapPauseBanner compact />

      {/* Repeat-incident detector — surfaces when the system has needed
          ≥ 4 manual cap overrides in 90 days, suggesting persistent
          deliverability hygiene problems that ride-the-cap won't fix. */}
      <RepeatIncidentBanner />

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <p className="text-xs text-[#525252] max-w-2xl">Automated sequences — trigger by manual enroll, list join, or scheduled date. Phase 3 v1 supports <code>send_email</code>, <code>delay</code>, <code>update_property</code>, and basic <code>if_else</code>.</p>
        <button onClick={() => setCreateOpen(true)}
                className="h-9 px-3 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                data-testid="new-workflow-btn">
          <Plus size={12} weight="bold" />New Workflow
        </button>
      </div>

      <div className="bg-white border border-[#E5E5E5]">
        {loading ? <p className="p-8 text-center text-sm text-[#A3A3A3]">Loading…</p>
        : rows.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-sm text-[#525252] mb-2">No workflows yet.</p>
            <button onClick={() => setCreateOpen(true)} className="text-[#0055FF] font-bold text-xs uppercase tracking-wider">Create one</button>
          </div>
        )
        : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-5 py-2 font-bold">Name</th>
                <th className="text-left px-3 py-2 font-bold">Trigger</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="text-right px-3 py-2 font-bold">Enrolled</th>
                <th className="text-right px-3 py-2 font-bold" title="Enrollments in the last 7 days">Last 7d</th>
                <th className="text-right px-3 py-2 font-bold">Created</th>
                <th className="text-right px-3 py-2 font-bold">Updated</th>
                <th className="text-right px-3 py-2 font-bold">Last run</th>
                <th className="text-right px-5 py-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(w => {
                const s = STATUS[w.status] || STATUS.draft;
                const canPauseResume = w.status === "active" || w.status === "paused";
                return (
                  <tr key={w.id} onClick={() => nav(`/marketing/workflows/${w.id}`)}
                      className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA] cursor-pointer" data-testid={`wf-row-${w.id}`}>
                    <td className="px-5 py-3 font-semibold text-[#0A0A0A]">{w.name}</td>
                    <td className="px-3 py-3 text-xs text-[#525252]">{w.trigger_type}</td>
                    <td className="px-3 py-3"><span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5" style={{ backgroundColor: s.bg, color: s.fg }}>{s.label}</span></td>
                    <td className="px-3 py-3 text-right tabular-nums text-xs text-[#525252]">{w.enrollment_total || 0}</td>
                    <td className="px-3 py-3 text-right tabular-nums whitespace-nowrap" data-testid={`wf-7d-${w.id}`}>
                      <EnrollmentSparkline data={w.enrollment_sparkline_7d} count={w.enrollment_recent_7d || 0} />
                    </td>
                    <td className="px-3 py-3 text-right text-xs text-[#A3A3A3] tabular-nums" title={w.created_at || ""}>{fmt(w.created_at)}</td>
                    <td className="px-3 py-3 text-right text-xs text-[#A3A3A3] tabular-nums" title={w.updated_at || ""}>{fmt(w.updated_at)}</td>
                    <td className="px-3 py-3 text-right text-xs text-[#A3A3A3] tabular-nums" title={w.last_run_at || ""}>{fmt(w.last_run_at)}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button onClick={(e) => { e.stopPropagation(); nav(`/marketing/workflows/${w.id}`); }}
                              title="Save / edit" className="p-1 text-[#525252] hover:text-[#0055FF]"
                              data-testid={`wf-edit-${w.id}`}>
                        <PencilSimple size={14} />
                      </button>
                      {canPauseResume ? (
                        <button onClick={(e) => pauseResume(w, e)}
                                title={w.status === "paused" ? "Resume" : "Pause"}
                                className={`p-1 ${w.status === "paused" ? "text-[#16A34A] hover:text-[#059669]" : "text-[#D97706] hover:text-[#B45309]"}`}
                                data-testid={`${w.status === "paused" ? "wf-resume" : "wf-pause"}-${w.id}`}>
                          {w.status === "paused" ? <Play size={14} /> : <Pause size={14} />}
                        </button>
                      ) : (
                        <span className="p-1 text-[#E5E5E5] inline-block cursor-not-allowed" title={`Cannot pause a ${w.status} workflow`}>
                          <Pause size={14} />
                        </span>
                      )}
                      {(() => {
                        const isProtected = w.id === "wf_inbound_jan"
                          || (w.name || "").toLowerCase().includes("inbound response to sales leads");
                        if (isProtected) {
                          return (
                            <span className="p-1 text-[#E5E5E5] inline-block cursor-not-allowed"
                                  title="Protected workflow — receives every new lead and cannot be deleted."
                                  data-testid={`wf-delete-disabled-${w.id}`}>
                              <Trash size={14} />
                            </span>
                          );
                        }
                        return (
                          <button onClick={(e) => remove(w, e)}
                                  title="Delete workflow" className="p-1 text-[#525252] hover:text-[#DC2626]"
                                  data-testid={`wf-delete-${w.id}`}>
                            <Trash size={14} />
                          </button>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setCreateOpen(false)}>
          <div className="bg-white border border-[#E5E5E5] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-black tracking-tight mb-3">New workflow</h3>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                   placeholder="e.g. Welcome Drip"
                   className="w-full h-10 px-3 border border-[#E5E5E5] mb-4" data-testid="wf-name" autoFocus />
            <div className="flex justify-end gap-2">
              <button onClick={() => setCreateOpen(false)} className="h-9 px-4 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider">Cancel</button>
              <button onClick={doCreate} className="h-9 px-4 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider" data-testid="wf-create-confirm">Create</button>
            </div>
          </div>
        </div>
      )}
    </MarketingShell>
  );
}

export function WorkflowEditorPage() {
  const { workflowId: rawId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "platform_admin";
  const [workflowId, setWorkflowId] = useState(rawId === "new" ? null : rawId);
  const [wf, setWf] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [pickedIds, setPickedIds] = useState([]);
  // Which detail tab is showing. Default to Analytics for users opening
  // an active workflow (the most common "why did I come here" answer),
  // and Workflow for drafts (no stats yet).
  // `?tab=enrollments` (the morning digest's workflow links) wins over
  // the tab remembered from the last visit.
  const loc = useLocation();
  const [tab, setTab] = useState(() => {
    const linked = new URLSearchParams(loc.search).get("tab");
    if (WORKFLOW_TABS.includes(linked)) return linked;
    try { return sessionStorage.getItem("pulse_wf_tab") || "workflow"; }
    catch { return "workflow"; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("pulse_wf_tab", tab); } catch { /* ignore */ }
  }, [tab]);

  // Bootstrap: if rawId === "new", create a fresh workflow (optionally from
  // a pending AI-generated draft in sessionStorage) and navigate to its id.
  useEffect(() => {
    (async () => {
      if (rawId !== "new") return;
      try {
        const pendingRaw = sessionStorage.getItem("pulse_ai_pending_workflow_draft");
        let payload = { name: "Untitled Workflow", steps: [], trigger_type: "manual" };
        let appliedDraft = false;
        if (pendingRaw) {
          try {
            const draft = JSON.parse(pendingRaw);
            payload = {
              name: draft.name || "Untitled Workflow",
              description: draft.description || null,
              trigger_type: draft.trigger_kind || draft.trigger_type || "manual",
              steps: Array.isArray(draft.steps) ? draft.steps : [],
            };
            appliedDraft = true;
          } catch { /* ignore malformed draft */ }
          sessionStorage.removeItem("pulse_ai_pending_workflow_draft");
        }
        const r = await api.post("/marketing/workflows", payload);
        const newId = r.data.id;
        if (appliedDraft) toast.success("AI draft applied");
        setWorkflowId(newId);
        nav(`/marketing/workflows/${newId}`, { replace: true });
      } catch {
        toast.error("Could not create workflow");
        nav("/marketing/workflows", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawId]);

  const reload = async () => {
    if (!workflowId) return;
    try {
      const [w, e, eng, fc] = await Promise.all([
        api.get(`/marketing/workflows/${workflowId}`),
        api.get(`/marketing/workflows/${workflowId}/enrollments?limit=5000`),
        api.get(`/marketing/workflows/${workflowId}/engagement`).catch(() => ({ data: null })),
        api.get(`/marketing/workflows/${workflowId}/forecast`).catch(() => ({ data: null })),
      ]);
      setWf(w.data); setEnrollments(e.data); setEngagement(eng.data); setForecast(fc.data); setDirty(false);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { if (workflowId) reload(); }, [workflowId]);

  const save = async () => {
    try {
      await api.put(`/marketing/workflows/${workflowId}`, {
        name: wf.name, description: wf.description, status: wf.status,
        trigger_type: wf.trigger_type, steps: wf.steps,
      });
      toast.success("Saved"); setDirty(false);
    } catch { toast.error("Save failed"); }
  };

  const updateStep = (idx, patch) => {
    const next = [...(wf.steps || [])];
    next[idx] = { ...next[idx], ...patch };
    setWf({ ...wf, steps: next }); setDirty(true);
  };
  const addStep = (type) => {
    const defaults = {
      send_email: { type: "send_email", subject: "Subject for {{first_name|default:there}}",
                    body_html: "<p>Hello {{first_name|default:there}}</p>" },
      delay: { type: "delay", minutes: 60 },
      update_property: { type: "update_property", property: "consent_status", value: "opt_in" },
      if_else: { type: "if_else", condition: { field: "country", op: "equals", value: "USA" },
                 yes_steps: [], no_steps: [] },
    }[type];
    setWf({ ...wf, steps: [...(wf.steps || []), defaults] });
    setDirty(true);
  };
  const removeStep = (idx) => {
    const next = [...(wf.steps || [])]; next.splice(idx, 1);
    setWf({ ...wf, steps: next }); setDirty(true);
  };

  const openEnroll = async () => {
    try {
      const r = await api.get("/marketing/contacts?limit=50");
      setContacts(r.data.items); setPickedIds([]); setEnrollOpen(true);
    } catch { toast.error("Failed"); }
  };
  const doEnroll = async () => {
    if (pickedIds.length === 0) return toast.error("Pick at least one contact");
    try {
      const r = await api.post(`/marketing/workflows/${workflowId}/enroll`, { contact_ids: pickedIds });
      toast.success(`Enrolled ${r.data.enrolled} contacts`);
      setEnrollOpen(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Bulk-enrol every consenting contact in one click. Runs a preview first
  // so the confirm() shows the exact eligible count. Idempotent on the
  // backend — safe if the user double-clicks.
  const doBulkEnroll = async () => {
    if (wf.status !== "active") return toast.error("Activate the workflow first");
    let preview;
    try {
      preview = await api.post(`/marketing/workflows/${workflowId}/bulk-enroll`, {
        preview: true, consent_status: "opted_in", exclude_suppressed: true,
      });
    } catch (e) { return toast.error(e.response?.data?.detail || "Preview failed"); }
    const eligible = preview.data?.eligible || 0;
    if (eligible === 0) return toast.info("No new eligible contacts to enroll");
    if (!window.confirm(
      `Enroll ${eligible} opted-in contact${eligible === 1 ? "" : "s"} into "${wf.name}"?\n\n` +
      `Only contacts with consent_status='opted_in' AND not suppressed AND not already ` +
      `actively enrolled will be added. This cannot be undone per-contact, but you can ` +
      `pause the workflow at any time.`
    )) return;
    try {
      const r = await api.post(`/marketing/workflows/${workflowId}/bulk-enroll`, {
        preview: false, consent_status: "opted_in", exclude_suppressed: true,
      });
      toast.success(`Enrolled ${r.data.enrolled} of ${r.data.eligible} eligible contacts`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk enroll failed"); }
  };

  // Simulate-reply harness. Exercises the downstream reply chain
  // (pause enrolment → timeline event → forward to sales@eyvo.com) on a
  // real CRM lead + mkt_contact, without requiring the operator to
  // actually hit Reply in an email client. Platform-admin only.
  const [simReplyOpen, setSimReplyOpen] = useState(false);
  const [simReply, setSimReply] = useState({ email: "", subject: "Re: simulated reply", body: "" });
  const [simReplyResult, setSimReplyResult] = useState(null);
  const [simReplyBusy, setSimReplyBusy] = useState(false);
  const openSimReply = () => {
    setSimReply({ email: "", subject: "Re: simulated reply",
                  body: "This is a simulated reply fired from the workflow testing harness to verify pause / forward chain." });
    setSimReplyResult(null);
    setSimReplyOpen(true);
  };
  const runSimReply = async () => {
    const email = (simReply.email || "").trim().toLowerCase();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      return toast.error("Enter a valid contact email");
    }
    setSimReplyBusy(true);
    setSimReplyResult(null);
    try {
      const r = await api.post(`/marketing/workflows/${workflowId}/simulate-reply`, {
        email, subject: simReply.subject, body: simReply.body,
      });
      setSimReplyResult(r.data);
      toast.success("Simulated reply fired");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Simulate reply failed"); }
    setSimReplyBusy(false);
  };

  // ── Approval actions ───────────────────────────────────────────────────
  const requestApproval = async () => {
    if (dirty) await save();
    try {
      const r = await api.post(`/marketing/workflows/${workflowId}/request-approval`);
      setWf({ ...wf, approval_status: r.data.approval_status });
      toast.success("Approval requested");
    } catch (e) { toast.error(e.response?.data?.detail || "Request failed"); }
  };
  const approveNow = async () => {
    const notes = window.prompt("Approval notes (optional):", "") || "";
    try {
      const r = await api.post(`/marketing/workflows/${workflowId}/approve`, { notes });
      setWf({ ...wf, approval_status: r.data.approval_status,
              approved_by: r.data.approved_by, approved_at: r.data.approved_at });
      toast.success("Workflow approved");
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };
  const rejectNow = async () => {
    const notes = window.prompt("Reason for rejection (required):", "");
    if (!notes) return;
    try {
      const r = await api.post(`/marketing/workflows/${workflowId}/reject`, { notes });
      setWf({ ...wf, approval_status: r.data.approval_status,
              approved_by: null, approved_at: null, approval_notes: notes });
      toast.success("Workflow rejected — back to draft");
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };

  // ── Step preview / test-send ───────────────────────────────────────────
  const [previewStep, setPreviewStep] = useState(null); // { idx, ...}
  const openPreview = async (idx) => {
    if (dirty) await save();
    try {
      const r = await api.post(`/marketing/workflows/${workflowId}/preview-step`, { step_index: idx });
      setPreviewStep({ idx, ...r.data });
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };
  const testSendStep = async (idx) => {
    if (dirty) await save();
    const defaultTo = user?.email || "";
    const to = window.prompt("Send a test to (comma-separated):", defaultTo);
    if (to === null) return;
    const recipients = (to || defaultTo).split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    if (recipients.length === 0) return toast.error("Need at least one recipient");
    try {
      const r = await api.post(`/marketing/workflows/${workflowId}/test-send-step`,
                               { step_index: idx, recipients });
      toast.success(`Sent ${r.data.sent} test email(s)${r.data.failed?.length ? ` (${r.data.failed.length} failed)` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test send failed"); }
  };

  if (!wf) return <MarketingShell><p className="text-sm text-[#A3A3A3]">Loading…</p></MarketingShell>;

  const s = STATUS[wf.status] || STATUS.draft;

  return (
    <MarketingShell>
      <div className="bg-white border border-[#E5E5E5] p-4 mb-4 flex items-center gap-3 flex-wrap">
        <input value={wf.name || ""} onChange={e => { setWf({ ...wf, name: e.target.value }); setDirty(true); }}
               className="text-lg font-black tracking-tight flex-1 bg-transparent focus:outline-none border-0 min-w-[200px]"
               style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="wf-name-input" />
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5" style={{ backgroundColor: s.bg, color: s.fg }}>{s.label}</span>
        {dirty && <span className="text-[10px] font-bold uppercase tracking-wider text-[#D97706]">Unsaved</span>}
        <button onClick={save} className="h-8 px-3 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#0055FF]" data-testid="wf-save">Save</button>
        <TestSequenceButton kind="workflow" id={workflowId} sequenceName={wf.name} className="h-8" />
        {wf.status === "draft" && (
          <button
            onClick={async () => {
              if (wf.approval_status !== "approved") {
                toast.error("Workflow must be approved by a platform admin before activation.");
                return;
              }
              setWf({ ...wf, status: "active" });
              try {
                await api.put(`/marketing/workflows/${workflowId}`, { status: "active" });
                toast.success("Activated");
                reload();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Activate failed");
                setWf({ ...wf, status: "draft" });
              }
            }}
            disabled={wf.approval_status !== "approved"}
            title={wf.approval_status !== "approved" ? "Needs admin approval first" : ""}
            className="h-8 px-3 text-xs font-bold uppercase tracking-wider bg-[#16A34A] text-white flex items-center gap-1 disabled:opacity-40"
            data-testid="wf-activate"
          >
            <Play size={10} weight="fill" />Activate
          </button>
        )}
        {wf.status === "active" && (
          <button onClick={async () => { await api.put(`/marketing/workflows/${workflowId}`, { status: "paused" }); reload(); toast.success("Paused"); }}
                  className="h-8 px-3 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] flex items-center gap-1" data-testid="wf-pause">
            <Pause size={10} />Pause
          </button>
        )}
        {wf.status === "paused" && (
          <button
            onClick={async () => {
              if (wf.approval_status !== "approved") {
                toast.error("Workflow must be approved by a platform admin before reactivation.");
                return;
              }
              try {
                await api.put(`/marketing/workflows/${workflowId}`, { status: "active" });
                toast.success("Resumed — first-day pulse will land in 24h");
                reload();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Resume failed");
              }
            }}
            disabled={wf.approval_status !== "approved"}
            title={wf.approval_status !== "approved" ? "Needs admin approval first" : "Resume sending. Adaptive cap will hold sends at 0/day until bounce rate drops below 5%."}
            className="h-8 px-3 text-xs font-bold uppercase tracking-wider bg-[#16A34A] text-white flex items-center gap-1 disabled:opacity-40"
            data-testid="wf-resume"
          >
            <Play size={10} weight="fill" />Resume
          </button>
        )}
        <button onClick={openEnroll} disabled={wf.status !== "active"}
                className="h-8 px-3 text-xs font-bold uppercase tracking-wider bg-[#0055FF] text-white disabled:opacity-40" data-testid="wf-enroll-btn">
          Enroll Contacts
        </button>
        <button onClick={doBulkEnroll} disabled={wf.status !== "active"}
                title="Enrol every opted-in contact that isn't already in this workflow. Runs a preview first so you can confirm the exact count."
                className="h-8 px-3 text-xs font-bold uppercase tracking-wider bg-[#0A0A0A] text-white disabled:opacity-40" data-testid="wf-bulk-enroll-btn">
          Bulk Enrol (opted-in)
        </button>
        {isAdmin && (
          <button onClick={openSimReply}
                  title="Fire the downstream reply chain (pause enrolment → timeline event → forward to sales@eyvo.com) against a real CRM lead, without requiring you to hit Reply in your inbox. Platform-admin only."
                  className="h-8 px-3 text-xs font-bold uppercase tracking-wider border border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white" data-testid="wf-simulate-reply-btn">
            Simulate Reply
          </button>
        )}
      </div>

      <WorkflowApprovalBar wf={wf} isAdmin={isAdmin}
                           onRequest={requestApproval}
                           onApprove={approveNow}
                           onReject={rejectNow} />

      {/* Deliverability kill-switch trip banner — red, persistent until the
          workflow is resumed. Clears automatically on the next reload after
          status flips back to 'active'. */}
      {wf.kill_switch && (
        <div className="bg-[#FEF2F2] border border-[#DC2626] p-4 mb-4" data-testid="wf-kill-switch-banner">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#DC2626] mb-1">
            🚨 Deliverability kill switch tripped
          </p>
          <p className="text-sm text-[#0A0A0A] mb-1">
            {wf.kill_switch.details}
          </p>
          <p className="text-xs text-[#525252]">
            Triggered at <code>{wf.kill_switch.triggered_at}</code>. An alert email was sent to the configured recipient. Resume the workflow below once the underlying cause is remediated.
          </p>
        </div>
      )}

      {/* Adaptive-cap banner — surfaces the global red/amber-band throttle
          on EVERY tab of the workflow detail so operators don't land on
          "Workflow" and wonder why nothing is shipping. Silent when band
          is green/starter. Separate code path from `wf.kill_switch` above
          (per-workflow trip vs. global rolling-rate throttle). */}
      <CapPauseBanner />

      <WorkflowTabNav
        tab={tab}
        onTab={setTab}
        counts={{
          enrollments: enrollments.length,
          clicks: engagement?.totals?.unique_clicks || 0,
        }} />

      {tab === "workflow" && (
      <>
      {/* Send-window policy reminder + next-send forecast — timezones
          rendered in the viewing user's local timezone so the operator
          can see EXACTLY when their queue will start firing without
          doing tz math in their head. */}
      <SendWindowBanner forecast={forecast} workflow={wf} />

      {/* Steps */}
      <div className="bg-white border border-[#E5E5E5] p-4 mb-4">
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-3">Steps</h2>
        <div className="space-y-2">
          {(wf.steps || []).map((st, i) => {
            const Icon = STEP_ICONS[st.type] || Envelope;
            return (
              <div key={i} className="border border-[#E5E5E5] p-3 flex items-start gap-3" data-testid={`wf-step-${i}`}>
                <div className="w-8 h-8 bg-[#EFF6FF] flex items-center justify-center text-[#0055FF] font-black text-sm tabular-nums">{i + 1}</div>
                <Icon size={18} className="text-[#525252] mt-1 shrink-0" />
                <div className="flex-1 space-y-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">{st.type.replace(/_/g, " ")}</p>
                  {st.type === "send_email" && (
                    <ABVariantEditor
                      step={st}
                      stepIndex={i}
                      workflowId={workflowId}
                      onPatch={(patch) => updateStep(i, patch)}
                      onOpenPreview={() => openPreview(i)}
                      onTestSend={() => testSendStep(i)}
                      engagement={engagement}
                    />
                  )}
                  {st.type === "delay" && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#525252]">Wait</span>
                      <input
                        type="number"
                        min={0}
                        step={0.25}
                        value={st.days ?? (st.minutes != null ? (st.minutes / 1440).toFixed(3).replace(/\.?0+$/, "") : 0)}
                        onChange={e => {
                          const v = parseFloat(e.target.value) || 0;
                          // Clear legacy `minutes` field once the user edits.
                          updateStep(i, { days: v, minutes: undefined });
                        }}
                        className="h-8 w-24 px-2 text-xs border border-[#E5E5E5] tabular-nums"
                        data-testid={`wf-step-${i}-days`}
                      />
                      <span className="text-xs text-[#525252]">days</span>
                    </div>
                  )}
                  {st.type === "update_property" && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <select value={st.property || "consent_status"} onChange={e => updateStep(i, { property: e.target.value })}
                              className="h-8 px-2 text-xs border border-[#E5E5E5]">
                        <option value="consent_status">consent_status</option>
                        <option value="consent_source">consent_source</option>
                        <option value="country">country</option>
                        <option value="job_title">job_title</option>
                      </select>
                      <span className="text-xs text-[#525252]">=</span>
                      <input value={st.value || ""} onChange={e => updateStep(i, { value: e.target.value })}
                             placeholder="new value" className="h-8 px-2 text-xs border border-[#E5E5E5]" />
                    </div>
                  )}
                  {st.type === "if_else" && (
                    <IfElseEditor step={st} onChange={patch => updateStep(i, patch)} />
                  )}
                </div>
                <button onClick={() => removeStep(i)} className="text-[#525252] hover:text-[#DC2626] p-1" data-testid={`wf-step-${i}-remove`}>
                  <Trash size={14} />
                </button>
              </div>
            );
          })}
          <div className="flex gap-2 pt-2">
            {[["send_email", "Send email"], ["delay", "Delay"], ["update_property", "Update property"], ["if_else", "If/else"]].map(([k, label]) => (
              <button key={k} onClick={() => addStep(k)}
                      className="h-8 px-3 border border-[#E5E5E5] hover:border-[#0055FF] text-[11px] font-bold uppercase tracking-wider flex items-center gap-1"
                      data-testid={`wf-add-${k}`}>
                <Plus size={10} weight="bold" />{label}
              </button>
            ))}
          </div>
        </div>
      </div>
      </>
      )}

      {tab === "analytics" && (
      <>
      {/* Engagement panel — clickable tiles + funnel chart + drill-down drawer */}
      <EngagementPanel engagement={engagement} workflowId={workflowId} />

      {/* Adaptive throughput cap — derived from rolling 7-day bounce
          rate. Surfaces "why is my cap 25 today?" inline. */}
      <AdaptiveCapStatus />

      {/* Operator-history of cap overrides — pattern recognition for
          recurring deliverability incidents. Self-hides when zero rows. */}
      <CapOverrideHistory />

      {/* Rolling 7-day bounce-rate trend — deliverability health at a glance */}
      <BounceTrendChart workflowId={workflowId} />

      {/* Statistical winner detector — surfaces the variant that's
          beating its peers with 95% confidence and offers a one-click
          pause for the worst loser. Pair-wise z-test on the chosen
          metric (real opens / clicks / replies). */}
      <VariantWinnerBanner workflowId={workflowId} />

      {/* A/B test controls + per-step variant analytics */}
      <ABTestPanel workflow={wf} workflowId={workflowId} isAdmin={isAdmin} onReload={reload} />
      </>
      )}

      {tab === "clicks" && (
        <ClicksTab workflowId={workflowId} />
      )}

      {tab === "enrollments" && (
        <EnrollmentsTab enrollments={enrollments} />
      )}

      {tab === "followup" && (
        <FollowUpTab workflowId={workflowId} />
      )}

      {tab === "manual-send" && (
        <ManualSendTab workflowId={workflowId} />
      )}

      {/* Enroll modal */}
      {enrollOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEnrollOpen(false)}>
          <div className="bg-white border border-[#E5E5E5] p-6 w-full max-w-lg max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-black tracking-tight mb-1">Enroll contacts</h3>
            <p className="text-xs text-[#525252] mb-3">Showing first 50 contacts. {pickedIds.length} selected.</p>
            <div className="space-y-1">
              {contacts.map(c => (
                <label key={c.id} className="flex items-center gap-2 p-1.5 hover:bg-[#FAFAFA] cursor-pointer text-sm">
                  <input type="checkbox" checked={pickedIds.includes(c.id)}
                         onChange={() => setPickedIds(pickedIds.includes(c.id)
                           ? pickedIds.filter(x => x !== c.id) : [...pickedIds, c.id])} />
                  <span className="flex-1">{c.email}</span>
                  <span className="text-xs text-[#A3A3A3]">{c.full_name || c.company_name || ""}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-[#F5F5F5]">
              <button onClick={() => setEnrollOpen(false)} className="h-9 px-4 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider">Cancel</button>
              <button onClick={doEnroll} className="h-9 px-4 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider" data-testid="enroll-confirm">
                Enroll {pickedIds.length || ""}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewStep && (
        <StepPreviewModal step={previewStep} onClose={() => setPreviewStep(null)} />
      )}

      {/* Simulate-reply modal — fires the downstream chain without needing
          an actual email reply. Platform-admin only. */}
      {simReplyOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setSimReplyOpen(false)}>
          <div className="bg-white border border-[#E5E5E5] p-6 w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="simulate-reply-dialog">
            <h3 className="text-lg font-black tracking-tight mb-1">Simulate inbound reply</h3>
            <p className="text-xs text-[#525252] mb-4">Exercises the downstream reply chain against a REAL contact: pauses their active enrolment in this workflow, logs a <code>replied</code> timeline event on the CRM lead, and forwards the faux reply to <code>sales@eyvo.com</code>. No outbound email is sent to the contact. Platform-admin only.</p>
            <label className="block text-[10px] font-bold text-[#525252] uppercase tracking-wider mb-1">Contact email</label>
            <input
              type="email"
              value={simReply.email}
              onChange={e => setSimReply(s => ({ ...s, email: e.target.value }))}
              placeholder="e.g. an enrolled contact's address"
              className="w-full rounded-none border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
              data-testid="sim-reply-email"
              autoFocus
            />
            <label className="block text-[10px] font-bold text-[#525252] uppercase tracking-wider mt-3 mb-1">Subject</label>
            <input
              type="text"
              value={simReply.subject}
              onChange={e => setSimReply(s => ({ ...s, subject: e.target.value }))}
              className="w-full rounded-none border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
              data-testid="sim-reply-subject"
            />
            <label className="block text-[10px] font-bold text-[#525252] uppercase tracking-wider mt-3 mb-1">Reply body</label>
            <textarea
              rows={4}
              value={simReply.body}
              onChange={e => setSimReply(s => ({ ...s, body: e.target.value }))}
              className="w-full rounded-none border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
              data-testid="sim-reply-body"
            />
            {simReplyResult && (
              <div className="mt-3 border border-[#16A34A] bg-[#F0FDF4] p-3 text-xs" data-testid="sim-reply-result">
                <p className="font-bold text-[#16A34A] mb-1">Chain fired ✓</p>
                <ul className="space-y-0.5 text-[#0A0A0A]">
                  <li>Matched lead: <code>{simReplyResult.lead_id}</code></li>
                  <li>Workflows paused: <strong>{simReplyResult.workflows_paused}</strong>{simReplyResult.had_active_enrolment_before ? "" : " (no active enrolment was present)"}</li>
                  <li>Forwarded to sales@eyvo.com: <strong>{simReplyResult.forwarded_to_sales ? "yes" : "no (no body)"}</strong></li>
                  <li className="text-[#525252]">Check the CRM lead timeline + sales@eyvo.com inbox to verify.</li>
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-5 pt-3 border-t border-[#F5F5F5]">
              <button onClick={() => setSimReplyOpen(false)} className="h-9 px-4 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider" data-testid="sim-reply-cancel">Close</button>
              <button onClick={runSimReply} disabled={simReplyBusy}
                      className="h-9 px-4 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50" data-testid="sim-reply-fire">
                {simReplyBusy ? "Firing…" : "Fire reply chain"}
              </button>
            </div>
          </div>
        </div>
      )}
    </MarketingShell>
  );
}


/* ───────── If/Else branch editor ───────── */
function IfElseEditor({ step, onChange }) {
  const cond = step.condition || { field: "country", op: "equals", value: "" };
  const yes = step.yes_steps || [];
  const no = step.no_steps || [];

  const setCond = (patch) => onChange({ condition: { ...cond, ...patch } });
  const addBranchStep = (branchKey, type) => {
    const defaults = {
      send_email: { type: "send_email", subject: "Subject", body_html: "<p>Hello</p>" },
      delay: { type: "delay", days: 1 },
      update_property: { type: "update_property", property: "consent_status", value: "opt_in" },
    }[type];
    const next = [...(step[branchKey] || []), defaults];
    onChange({ [branchKey]: next });
  };
  const updateBranchStep = (branchKey, idx, patch) => {
    const next = [...(step[branchKey] || [])];
    next[idx] = { ...next[idx], ...patch };
    onChange({ [branchKey]: next });
  };
  const removeBranchStep = (branchKey, idx) => {
    const next = [...(step[branchKey] || [])];
    next.splice(idx, 1);
    onChange({ [branchKey]: next });
  };

  return (
    <div className="space-y-2 mt-1" data-testid="if-else-editor">
      <div className="flex items-center gap-1.5 flex-wrap bg-[#FAFAFA] p-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">If</span>
        <select value={cond.field} onChange={e => setCond({ field: e.target.value })}
                className="h-7 px-1.5 text-xs border border-[#E5E5E5] bg-white">
          {["country", "consent_status", "company_name", "job_title", "first_name", "last_name"].map(f =>
            <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={cond.op} onChange={e => setCond({ op: e.target.value })}
                className="h-7 px-1.5 text-xs border border-[#E5E5E5] bg-white">
          {["equals", "not_equals", "contains", "not_contains", "is_set", "not_set"].map(o =>
            <option key={o} value={o}>{o.replace("_", " ")}</option>)}
        </select>
        {!["is_set", "not_set"].includes(cond.op) && (
          <input value={cond.value || ""} onChange={e => setCond({ value: e.target.value })}
                 placeholder="value" className="h-7 px-2 text-xs border border-[#E5E5E5] bg-white min-w-[120px]" />
        )}
      </div>

      {[["yes_steps", "Then (if true)"], ["no_steps", "Else (if false)"]].map(([k, label]) => (
        <div key={k} className="border-l-2 border-[#E5E5E5] pl-3 space-y-1.5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">{label}</p>
          {(step[k] || []).length === 0 && (
            <p className="text-[11px] text-[#A3A3A3] italic">No steps yet.</p>
          )}
          {(step[k] || []).map((s, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs" data-testid={`if-${k}-${i}`}>
              <span className="text-[10px] text-[#A3A3A3] uppercase tracking-wider w-24">{s.type.replace("_", " ")}</span>
              {s.type === "send_email" && (
                <input value={s.subject || ""} onChange={e => updateBranchStep(k, i, { subject: e.target.value })}
                       placeholder="Subject" className="h-7 px-2 flex-1 text-xs border border-[#E5E5E5]" />
              )}
              {s.type === "delay" && (
                <>
                  <input type="number" min={0} value={s.minutes || 0}
                         onChange={e => updateBranchStep(k, i, { minutes: parseInt(e.target.value) || 0 })}
                         className="h-7 w-16 px-1 text-xs border border-[#E5E5E5] tabular-nums" />
                  <span className="text-[11px] text-[#A3A3A3]">min</span>
                </>
              )}
              {s.type === "update_property" && (
                <>
                  <select value={s.property || ""} onChange={e => updateBranchStep(k, i, { property: e.target.value })}
                          className="h-7 px-1 text-xs border border-[#E5E5E5]">
                    {["consent_status", "consent_source", "country", "job_title"].map(p =>
                      <option key={p}>{p}</option>)}
                  </select>
                  <span className="text-[11px]">=</span>
                  <input value={s.value || ""} onChange={e => updateBranchStep(k, i, { value: e.target.value })}
                         className="h-7 px-2 flex-1 text-xs border border-[#E5E5E5]" />
                </>
              )}
              <button onClick={() => removeBranchStep(k, i)} className="text-[#525252] hover:text-[#DC2626] p-0.5">
                <Trash size={11} />
              </button>
            </div>
          ))}
          <div className="flex gap-1">
            {["send_email", "delay", "update_property"].map(t => (
              <button key={t} onClick={() => addBranchStep(k, t)}
                      className="h-6 px-2 border border-[#E5E5E5] hover:border-[#0055FF] text-[10px] font-bold uppercase tracking-wider"
                      data-testid={`if-add-${k}-${t}`}>
                + {t.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ───────── Workflow email body — Blocks ↔ HTML toggle ───────── */
function EmailStepBody({ html, onChange, testPrefix, defaultOpen = false, instanceKey }) {
  const initialBlocks = extractBlocks(html);
  const [mode, setMode] = useState(initialBlocks ? "blocks" : "html");
  const [blocks, setBlocks] = useState(initialBlocks || emptyBlocks());
  const [open, setOpen] = useState(defaultOpen);

  // When the caller swaps `instanceKey` (e.g. switching between A/B/C
  // variant tabs), the `html` prop changes but our local `blocks`/`mode`
  // state was seeded on MOUNT — without this effect the designer keeps
  // rendering the previous variant's content while the user thinks they
  // are editing the new one. Re-seed everything from the incoming html.
  React.useEffect(() => {
    const fresh = extractBlocks(html) || blocksFromRawHtml(html || "");
    setBlocks(fresh);
    setMode(extractBlocks(html) ? "blocks" : "html");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceKey]);

  const syncBlocks = (next) => {
    setBlocks(next);
    const visible = renderBlocksToHtml(next);
    onChange(wrapBlocksInHtml(next, visible));
  };

  const switchToBlocks = () => {
    const found = extractBlocks(html);
    // If no block marker is present in the HTML, preserve whatever the user
    // typed by wrapping it as a single custom_html block so both panes stay
    // in sync with the same content.
    const seed = found || blocksFromRawHtml(html);
    setBlocks(seed);
    const visible = renderBlocksToHtml(seed);
    onChange(wrapBlocksInHtml(seed, visible));
    setMode("blocks");
  };

  const switchToHtml = () => {
    onChange(renderBlocksToHtml(blocks));
    setMode("html");
  };

  // Compact inline preview in collapsed state
  const previewText = (() => {
    const cleaned = (html || "").replace(/<!--[\s\S]*?-->/g, "").replace(/<[^>]*>/g, " ").trim();
    return cleaned.slice(0, 120) + (cleaned.length > 120 ? "…" : "");
  })();

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full text-left bg-[#FAFAFA] border border-[#E5E5E5] px-3 py-2 text-xs text-[#525252] hover:border-[#0055FF] transition-colors"
        data-testid={`${testPrefix}-open-body`}
      >
        <span className="font-bold uppercase tracking-wider text-[#0055FF] text-[10px] mr-2">Design email</span>
        {previewText || <span className="italic text-[#A3A3A3]">No content yet — click to start</span>}
      </button>
    );
  }

  return (
    <div className="border border-[#0055FF] bg-white p-3 space-y-2" data-testid={`${testPrefix}-body`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 bg-[#F5F5F5] p-0.5 w-fit">
          <button type="button" onClick={switchToBlocks}
                  className={`px-3 h-6 text-[10px] font-bold uppercase tracking-wider ${mode === "blocks" ? "bg-white shadow-sm text-[#0055FF]" : "text-[#525252]"}`}
                  data-testid={`${testPrefix}-mode-blocks`}>Designer</button>
          <button type="button" onClick={switchToHtml}
                  className={`px-3 h-6 text-[10px] font-bold uppercase tracking-wider ${mode === "html" ? "bg-white shadow-sm text-[#0055FF]" : "text-[#525252]"}`}
                  data-testid={`${testPrefix}-mode-html`}>HTML</button>
        </div>
        <button type="button" onClick={() => setOpen(false)}
                className="text-[10px] font-bold uppercase tracking-wider text-[#525252] hover:text-[#0055FF]"
                data-testid={`${testPrefix}-collapse-body`}>
          Collapse
        </button>
      </div>
      {mode === "blocks" ? (
        <EmailBlockBuilder blocks={blocks} onBlocksChange={syncBlocks} />
      ) : (
        <textarea value={html || ""} onChange={(e) => onChange(e.target.value)}
                  placeholder="<p>Hi {{first_name|default:there}}!</p>"
                  className="w-full h-40 px-3 py-2 text-xs font-mono border border-[#E5E5E5] bg-[#FAFAFA]"
                  data-testid={`${testPrefix}-body-html`} />
      )}
    </div>
  );
}



/* ───────── Workflow approval bar ───────── */
const WF_APPROVAL_STATES = {
  draft: { bg: "#F5F5F5", fg: "#525252", label: "Draft — not submitted", Icon: Clock },
  pending_approval: { bg: "#FFFBEB", fg: "#92400E", label: "Pending admin approval", Icon: Clock },
  approved: { bg: "#F0FDF4", fg: "#15803D", label: "Approved", Icon: ShieldCheck },
};

function WorkflowApprovalBar({ wf, isAdmin, onRequest, onApprove, onReject }) {
  const st = wf.approval_status || "draft";
  const meta = WF_APPROVAL_STATES[st] || WF_APPROVAL_STATES.draft;
  const Icon = meta.Icon;
  return (
    <div
      className="border p-3 mb-4 flex items-center gap-3 flex-wrap text-xs"
      style={{ backgroundColor: meta.bg, borderColor: meta.fg }}
      data-testid="wf-approval-bar"
    >
      <Icon size={14} style={{ color: meta.fg }} />
      <span className="font-bold uppercase tracking-wider" style={{ color: meta.fg }}>{meta.label}</span>
      {st === "approved" && wf.approved_at && (
        <span className="text-[11px] text-[#525252]">· approved {formatLocalDate(wf.approved_at)}</span>
      )}
      {wf.approval_notes && (
        <span className="text-[11px] text-[#525252] italic truncate max-w-[40ch]">Note: {wf.approval_notes}</span>
      )}
      <div className="ml-auto flex items-center gap-2">
        {st === "draft" && (
          <button onClick={onRequest}
                  className="h-8 px-3 bg-[#0A0A0A] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#0055FF]"
                  data-testid="wf-request-approval-btn">
            Request approval
          </button>
        )}
        {isAdmin && st === "pending_approval" && (
          <>
            <button onClick={onApprove}
                    className="h-8 px-3 bg-[#16A34A] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#15803D] flex items-center gap-1"
                    data-testid="wf-approve-btn">
              <Check size={11} weight="bold" />Approve
            </button>
            <button onClick={onReject}
                    className="h-8 px-3 border border-[#DC2626] text-[#DC2626] text-[11px] font-bold uppercase tracking-wider hover:bg-[#FEF2F2] flex items-center gap-1"
                    data-testid="wf-reject-btn">
              <X size={11} weight="bold" />Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ───────── Per-step deliverability chip with expandable issues ───────── */
function StepDeliverabilityChip({ workflowId, stepIndex, subject, bodyHtml }) {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!workflowId) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.post(`/marketing/workflows/${workflowId}/lint-step`, { step_index: stepIndex });
        setScore(r.data);
      } catch { /* ignore */ }
      setLoading(false);
    }, 800);
    return () => clearTimeout(t);
  }, [subject, bodyHtml, workflowId, stepIndex]);

  if (!score && !loading) return null;
  const color = !score ? "#A3A3A3"
    : score.score >= 85 ? "#16A34A"
    : score.score >= 70 ? "#0055FF"
    : score.score >= 50 ? "#D97706" : "#DC2626";
  return (
    <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-2" data-testid={`wf-step-${stepIndex}-lint`}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 w-full text-left">
        <span className="text-xl font-black tabular-nums" style={{ color, fontFamily: "Cabinet Grotesk, sans-serif" }}>
          {loading ? "…" : score.score}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>
          {loading ? "Checking…" : score.tier?.replace(/_/g, " ")}
        </span>
        <span className="text-[10px] text-[#A3A3A3]">· Deliverability</span>
        {score?.issues?.length > 0 && (
          <span className="ml-auto text-[10px] text-[#525252]">
            {open ? "Hide" : "View"} {score.issues.length} issue{score.issues.length === 1 ? "" : "s"}
          </span>
        )}
      </button>
      {open && score?.issues?.length > 0 && (
        <ul className="text-[11px] space-y-1 mt-2" data-testid={`wf-step-${stepIndex}-lint-issues`}>
          {score.issues.map((iss, i) => {
            const sev = iss.severity || "low";
            const label = sev === "high" ? "Critical" : sev === "medium" ? "Warning" : "Suggestion";
            return (
              <li key={i} className="flex gap-1.5 items-start">
                <span
                  className={`text-[9px] font-bold uppercase tracking-wider px-1 py-[1px] flex-shrink-0 ${sev === "high" ? "bg-[#FEF2F2] text-[#DC2626]" : sev === "medium" ? "bg-[#FFFBEB] text-[#D97706]" : "bg-[#F0F7FF] text-[#0055FF]"}`}
                  title={`Severity: ${sev}. This is the impact of this single issue, not the overall score.`}
                >
                  {label}
                </span>
                <span className="text-[#525252]">{iss.msg || iss.message}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function StepEngagementBadge({ engagement, stepIndex }) {
  const row = engagement?.per_step?.find(p => p.step_index === stepIndex);
  if (!row || !row.sent) {
    return (
      <div className="flex items-center gap-1 text-[10px] text-[#A3A3A3]" data-testid={`wf-step-${stepIndex}-engagement`}>
        <Eye size={11} /> No sends yet
      </div>
    );
  }
  const op = (row.open_rate * 100).toFixed(0);
  const cp = (row.click_rate * 100).toFixed(0);
  const colour = row.open_rate >= 0.3 ? "text-[#16A34A]" : row.open_rate >= 0.15 ? "text-[#0055FF]" : "text-[#A3A3A3]";
  return (
    <div className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider ${colour}`} data-testid={`wf-step-${stepIndex}-engagement`}>
      <span>Sent {row.sent}</span>
      <span>·</span>
      <span>Opened {row.unique_opens} ({op}%)</span>
      <span>·</span>
      <span className="text-[#525252]">Clicks {row.unique_clicks} ({cp}%)</span>
    </div>
  );
}

/* ───────── 7-day enrollment sparkline (workflow-card badge) ─────────
   Tiny inline SVG. Last bar is "today" (rightmost). Empty data ⇒ shows
   "—" so the user instantly sees inbound has dried up.
   Dimensions are tuned to fit comfortably inside the table cell next
   to the count. */
function EnrollmentSparkline({ data, count }) {
  const points = Array.isArray(data) && data.length === 7 ? data : [0,0,0,0,0,0,0];
  const max = Math.max(1, ...points);
  const w = 56, h = 18, gap = 2;
  const barW = (w - gap * 6) / 7;
  const days = ["6d ago","5d ago","4d ago","3d ago","2d ago","Yesterday","Today"];
  const tooltip = points.map((n, i) => `${days[i]}: ${n}`).join("\n") + `\nTotal last 7d: ${count}`;
  const isDry = count === 0;
  const fill = isDry ? "#E5E5E5" : "#0055FF";
  return (
    <span className="inline-flex items-center gap-1.5" title={tooltip} data-testid="wf-sparkline">
      <span className={`text-xs font-bold tabular-nums ${isDry ? "text-[#A3A3A3]" : "text-[#0055FF]"}`}>{count}</span>
      <svg width={w} height={h} aria-hidden="true" className="block">
        {points.map((n, i) => {
          const barH = Math.max(1, (n / max) * (h - 2));
          return (
            <rect
              key={i}
              x={i * (barW + gap)}
              y={h - barH}
              width={barW}
              height={barH}
              fill={fill}
              opacity={i === 6 ? 1 : 0.55}
            />
          );
        })}
      </svg>
    </span>
  );
}

// ── A/B Test Panel (session 60) ──────────────────────────────────────────
// Workflow-level control + per-step variant analytics. Enables/disables
// the 10%-holdout cohort, shows cohort counts, and renders a comparison
// table for every step that has competing variants. Winner promotion
// button wins on reply rate per the product spec.
function ABTestPanel({ workflow, workflowId, isAdmin, onReload }) {
  const [analytics, setAnalytics] = useState(null);
  const [busy, setBusy] = useState(false);
  const cfg = workflow?.ab_config || null;
  const abEnabled = !!cfg;

  const load = React.useCallback(async () => {
    if (!workflowId) return;
    try {
      const r = await api.get(`/marketing/workflows/${workflowId}/variant-analytics`);
      setAnalytics(r.data);
    } catch { /* silent — analytics is a progressive-enhancement surface */ }
  }, [workflowId]);
  React.useEffect(() => { load(); }, [load, workflow?.updated_at]);

  const toggleAB = async () => {
    setBusy(true);
    try {
      if (abEnabled) {
        if (!window.confirm("Disable A/B testing on this workflow? Existing variant copy is preserved, but new enrolments will no longer be split into test/pending cohorts and any pending stalls will catch up on the next tick.")) {
          setBusy(false); return;
        }
        await api.put(`/marketing/workflows/${workflowId}/ab-config`, { enabled: false });
        toast.success("A/B testing disabled");
      } else {
        const pct = window.prompt("Holdout cohort size (% of new enrolees who will be split across variants — the rest stall until you promote a winner). Enter a value between 1 and 99:", "10");
        const n = parseFloat(pct);
        if (!(n >= 1 && n <= 99)) { setBusy(false); return; }
        await api.put(`/marketing/workflows/${workflowId}/ab-config`, { enabled: true, holdout_pct: n });
        toast.success(`A/B testing enabled — ${n}% holdout cohort`);
      }
      onReload && onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const promote = async (stepIndex, variantId) => {
    if (!window.confirm(
      `Promote Variant ${variantId} as the winner for step ${stepIndex + 1}? ` +
      `Every future send for this step — test and pending cohorts alike — will use this copy. ` +
      `Pending enrolees currently stalled on this step will start again immediately.`
    )) return;
    try {
      await api.post(`/marketing/workflows/${workflowId}/steps/${stepIndex}/promote-winner`,
                     { variant_id: variantId });
      toast.success(`Variant ${variantId} promoted — pending enrolees nudged`);
      load();
      onReload && onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Promotion failed");
    }
  };

  const hasAnySplit = analytics?.steps?.some(s => (s.variants || []).length >= 2);

  return (
    <div className="bg-white border border-[#E5E5E5] mb-4">
      <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-center justify-between gap-2">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#7C3AED]">A/B Test</h2>
          <p className="text-[11px] text-[#525252] mt-0.5">
            {abEnabled
              ? <>Holdout cohort: <strong>{cfg.holdout_pct}%</strong> of new enrolees receive random variants; remaining <strong>{100 - cfg.holdout_pct}%</strong> stall on split steps until you promote a winner.</>
              : "Off. Enable to run a 10% holdout test before the full blast."}
          </p>
        </div>
        {isAdmin && (
          <button onClick={toggleAB} disabled={busy}
                  className={`h-8 px-3 text-xs font-bold uppercase tracking-wider disabled:opacity-50 ${
                    abEnabled ? "border border-[#E5E5E5] text-[#525252]" : "bg-[#7C3AED] text-white"
                  }`} data-testid="wf-ab-toggle">
            {abEnabled ? "Disable A/B" : "Enable A/B"}
          </button>
        )}
      </div>
      {abEnabled && analytics && (
        <div className="px-4 py-3 space-y-3">
          <div className="flex gap-3 text-[11px] text-[#525252]">
            <span>Cohort:</span>
            <span className="font-mono"><strong>{analytics.cohort_counts?.test || 0}</strong> test</span>
            <span className="font-mono"><strong>{analytics.cohort_counts?.pending || 0}</strong> pending</span>
            {analytics.cohort_counts?.null ? (
              <span className="font-mono text-[#A3A3A3]"><strong>{analytics.cohort_counts.null}</strong> pre-AB</span>
            ) : null}
          </div>
          {!hasAnySplit && (
            <p className="text-[11px] text-[#A3A3A3] italic">No step has an A/B split yet. Click "+ Add A/B variant" on any send-email step above to start a test.</p>
          )}
          {(analytics.steps || []).filter(s => s.variants.length >= 2).map(s => (
            <div key={s.step_index} className="border border-[#E5E5E5]" data-testid={`wf-ab-step-${s.step_index}`}>
              <div className="px-3 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5] flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Step {s.step_index + 1}</p>
                {s.winner_variant_id && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-[#FEF3C7] text-[#92400E] border border-[#F59E0B]">
                    Winner: {s.winner_variant_id}
                  </span>
                )}
              </div>
              <table className="w-full text-xs">
                <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold w-12">Variant</th>
                    <th className="text-left px-3 py-2 font-bold">Subject</th>
                    <th className="text-right px-2 py-2 font-bold">Sends</th>
                    <th className="text-right px-2 py-2 font-bold">Opens</th>
                    <th className="text-right px-2 py-2 font-bold">Clicks</th>
                    <th className="text-right px-2 py-2 font-bold">Replies</th>
                    <th className="text-right px-2 py-2 font-bold">Bounces</th>
                    <th className="text-right px-2 py-2 font-bold">Unsub</th>
                    {!s.winner_variant_id && isAdmin && <th className="px-2 py-2"></th>}
                  </tr>
                </thead>
                <tbody>
                  {s.variants.map(v => {
                    const isWinner = s.winner_variant_id === v.id;
                    return (
                      <tr key={v.id} className={`border-t border-[#F5F5F5] ${isWinner ? "bg-[#FFFBEB]" : ""}`}>
                        <td className="px-3 py-2 font-bold">{v.id}{isWinner ? " ★" : ""}</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-[#525252] truncate max-w-[280px]" title={v.subject}>{v.subject}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{v.sends}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{v.opens} <span className="text-[#A3A3A3] text-[10px]">({v.open_rate}%)</span></td>
                        <td className="px-2 py-2 text-right tabular-nums">{v.clicks} <span className="text-[#A3A3A3] text-[10px]">({v.click_rate}%)</span></td>
                        <td className="px-2 py-2 text-right tabular-nums font-bold">{v.replies} <span className="text-[#A3A3A3] text-[10px] font-normal">({v.reply_rate}%)</span></td>
                        <td className="px-2 py-2 text-right tabular-nums">{v.bounces} <span className="text-[#A3A3A3] text-[10px]">({v.bounce_rate}%)</span></td>
                        <td className="px-2 py-2 text-right tabular-nums">{v.unsubscribes} <span className="text-[#A3A3A3] text-[10px]">({v.unsubscribe_rate}%)</span></td>
                        {!s.winner_variant_id && isAdmin && (
                          <td className="px-2 py-1">
                            <button onClick={() => promote(s.step_index, v.id)}
                                    className="h-6 px-2 text-[10px] font-bold uppercase tracking-wider border border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white"
                                    data-testid={`wf-ab-promote-${s.step_index}-${v.id}`}>
                              Promote
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="px-3 py-1.5 text-[10px] text-[#A3A3A3] border-t border-[#F5F5F5]">
                Winner is declared on <strong>reply rate</strong>. Replies are the metric that matters for cold outreach; opens &amp; clicks are shown for context.
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── A/B Variant editor (session 60) ──────────────────────────────────────
// Wraps the subject + body for a `send_email` step with optional A/B/C
// variant tabs. When variants are present, operators can edit each
// variant's subject & body independently and click "Test Variant X" to
// receive a single-variant preview email. Winner promotion is surfaced
// via a yellow pill when set.
function ABVariantEditor({ step, stepIndex, workflowId, onPatch, onOpenPreview, onTestSend, engagement }) {
  const variants = Array.isArray(step.variants) ? step.variants : [];
  const hasVariants = variants.length >= 2;
  const winnerId = step.winner_variant_id || null;
  const [activeVariantId, setActiveVariantId] = useState(hasVariants ? variants[0].id : null);

  // Ensure the active tab is one that actually exists (e.g. after delete).
  React.useEffect(() => {
    if (hasVariants && !variants.find(v => v.id === activeVariantId)) {
      setActiveVariantId(variants[0].id);
    }
  }, [hasVariants, variants, activeVariantId]);

  const addVariant = () => {
    // Start a split: copy the current (default) subject/body into variant A,
    // and seed a blank variant B for the operator to edit.
    if (variants.length === 0) {
      onPatch({
        variants: [
          { id: "A", subject: step.subject || "", body_html: step.body_html || "" },
          { id: "B", subject: step.subject || "", body_html: step.body_html || "" },
        ],
      });
      setActiveVariantId("A");
    } else if (variants.length < 3) {
      const nextLetter = String.fromCharCode(65 + variants.length); // C then (capped)
      onPatch({
        variants: [
          ...variants,
          { id: nextLetter, subject: variants[0].subject || "", body_html: variants[0].body_html || "" },
        ],
      });
      setActiveVariantId(nextLetter);
    }
  };

  const removeVariant = (id) => {
    if (variants.length <= 2) {
      // Collapse back to no-split: merge variant A's copy into step defaults.
      const survivor = variants.find(v => v.id !== id) || variants[0];
      onPatch({
        subject: survivor.subject || step.subject || "",
        body_html: survivor.body_html || step.body_html || "",
        variants: undefined,
        winner_variant_id: undefined,
      });
      return;
    }
    const next = variants.filter(v => v.id !== id);
    onPatch({ variants: next,
              winner_variant_id: winnerId === id ? undefined : winnerId });
    if (activeVariantId === id && next.length) setActiveVariantId(next[0].id);
  };

  const patchVariant = (id, patch) => {
    onPatch({ variants: variants.map(v => (v.id === id ? { ...v, ...patch } : v)) });
  };

  const sendTestVariant = async (id) => {
    const to = window.prompt(`Send variant ${id} of step ${stepIndex + 1} to which email address?`, "");
    if (!to) return;
    try {
      await api.post(`/marketing/workflows/${workflowId}/steps/${stepIndex}/test-variant`,
                     { variant_id: id, recipient_email: to.trim() });
      toast.success(`Variant ${id} sent to ${to.trim()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test variant send failed");
    }
  };

  if (!hasVariants) {
    // Classic single-copy editor (unchanged).
    return (
      <div className="space-y-2">
        <input value={step.subject || ""} onChange={e => onPatch({ subject: e.target.value })}
               placeholder="Subject" className="h-8 px-2 text-xs border border-[#E5E5E5] w-full"
               data-testid={`wf-step-${stepIndex}-subject`} />
        <EmailStepBody html={step.body_html || ""}
                       onChange={html => onPatch({ body_html: html })}
                       testPrefix={`wf-step-${stepIndex}`} />
        <StepDeliverabilityChip workflowId={workflowId} stepIndex={stepIndex}
                                subject={step.subject} bodyHtml={step.body_html} />
        <StepEngagementBadge engagement={engagement} stepIndex={stepIndex} />
        <div className="flex gap-2 pt-1">
          <button onClick={onOpenPreview}
                  className="h-7 px-2 text-[10px] font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#0055FF] flex items-center gap-1"
                  data-testid={`wf-step-${stepIndex}-preview`}>
            <Eye size={11} />Preview
          </button>
          <button onClick={onTestSend}
                  className="h-7 px-2 text-[10px] font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#0055FF] flex items-center gap-1"
                  data-testid={`wf-step-${stepIndex}-test-send`}>
            <PaperPlaneRight size={11} />Test send
          </button>
          <button onClick={addVariant}
                  className="h-7 px-2 text-[10px] font-bold uppercase tracking-wider border border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white flex items-center gap-1"
                  data-testid={`wf-step-${stepIndex}-add-variant`}>
            + Add A/B variant
          </button>
        </div>
      </div>
    );
  }

  const active = variants.find(v => v.id === activeVariantId) || variants[0];
  // Derive a "bodies-identical" warning so the user immediately sees
  // whether their test will actually vary the body or only the subject.
  // Strips block-marker comments + collapses whitespace so two bodies
  // that differ only in block serialization still compare equal.
  const normalizeBody = (s) => (s || "").replace(/<!--[\s\S]*?-->/g, "").replace(/\s+/g, " ").trim();
  const distinctBodies = new Set(variants.map(v => normalizeBody(v.body_html)));
  const bodiesIdentical = variants.length >= 2 && distinctBodies.size === 1;
  const distinctSubjects = new Set(variants.map(v => (v.subject || "").trim()));
  const subjectsIdentical = variants.length >= 2 && distinctSubjects.size === 1;
  return (
    <div className="space-y-2 border-l-2 border-[#7C3AED] pl-3">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#7C3AED]">A/B Split</span>
        {winnerId && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-[#FEF3C7] text-[#92400E] border border-[#F59E0B]"
                data-testid={`wf-step-${stepIndex}-winner-badge`}>
            Winner: {winnerId}
          </span>
        )}
      </div>
      <div className="flex gap-1 border-b border-[#E5E5E5]">
        {variants.map(v => (
          <button key={v.id} onClick={() => setActiveVariantId(v.id)}
                  className={`h-7 px-3 text-[11px] font-bold uppercase tracking-wider ${
                    activeVariantId === v.id
                      ? "border border-b-0 border-[#7C3AED] bg-[#F5F3FF] text-[#7C3AED]"
                      : "border border-transparent text-[#525252] hover:text-[#0A0A0A]"
                  }`}
                  data-testid={`wf-step-${stepIndex}-variant-tab-${v.id}`}>
            Variant {v.id}{winnerId === v.id ? " ★" : ""}
          </button>
        ))}
        {variants.length < 3 && (
          <button onClick={addVariant} title="Add another variant (max 3)"
                  className="h-7 px-2 text-[11px] font-bold uppercase tracking-wider text-[#7C3AED] hover:bg-[#F5F3FF]"
                  data-testid={`wf-step-${stepIndex}-variant-add`}>
            +
          </button>
        )}
      </div>
      <input value={active.subject || ""} onChange={e => patchVariant(active.id, { subject: e.target.value })}
             placeholder={`Subject (variant ${active.id})`}
             className="h-8 px-2 text-xs border border-[#E5E5E5] w-full"
             data-testid={`wf-step-${stepIndex}-variant-${active.id}-subject`} />
      <EmailStepBody key={`variant-body-${active.id}`}
                     instanceKey={`variant-body-${active.id}`}
                     html={active.body_html || ""}
                     onChange={html => patchVariant(active.id, { body_html: html })}
                     testPrefix={`wf-step-${stepIndex}-variant-${active.id}`} />
      {(bodiesIdentical || subjectsIdentical) && (
        <div className="text-[10px] px-2 py-1.5 bg-[#FEF3C7] text-[#92400E] border border-[#F59E0B] flex items-start gap-2"
             data-testid={`wf-step-${stepIndex}-variants-identical-warning`}>
          <span className="font-bold uppercase tracking-wider">⚠ Variants look identical</span>
          <span>
            {bodiesIdentical && subjectsIdentical && "All variants have the same subject AND body — edit each tab to actually split-test."}
            {bodiesIdentical && !subjectsIdentical && "Only subjects differ. Bodies are identical across all variants — switch to each tab and edit the body to fully split-test."}
            {!bodiesIdentical && subjectsIdentical && "Only bodies differ. Subjects are identical across all variants."}
          </span>
        </div>
      )}
      {variants.length === 2 && (
        <div className="text-[10px] text-[#7C3AED] italic flex items-center gap-2 pt-1">
          Testing 2 variants. You can add a third (A/B/C) —
          <button type="button" onClick={addVariant}
                  className="underline font-bold uppercase tracking-wider hover:text-[#5B21B6]"
                  data-testid={`wf-step-${stepIndex}-variant-add-c-hint`}>
            + Add variant C
          </button>
        </div>
      )}      <StepDeliverabilityChip workflowId={workflowId} stepIndex={stepIndex}
                              subject={active.subject} bodyHtml={active.body_html} />
      <StepEngagementBadge engagement={engagement} stepIndex={stepIndex} />
      <div className="flex gap-2 pt-1 flex-wrap">
        <button onClick={onOpenPreview}
                className="h-7 px-2 text-[10px] font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#0055FF] flex items-center gap-1"
                data-testid={`wf-step-${stepIndex}-preview`}>
          <Eye size={11} />Preview step
        </button>
        <button onClick={() => sendTestVariant(active.id)}
                className="h-7 px-2 text-[10px] font-bold uppercase tracking-wider border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white flex items-center gap-1"
                data-testid={`wf-step-${stepIndex}-variant-${active.id}-test`}>
          <PaperPlaneRight size={11} />Test variant {active.id}
        </button>
        <button onClick={onTestSend}
                className="h-7 px-2 text-[10px] font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#0055FF] flex items-center gap-1"
                title="Send a single test of this step (the active variant) to an email you specify."
                data-testid={`wf-step-${stepIndex}-test-send`}>
          <PaperPlaneRight size={11} />Test send (step)
        </button>
        <button onClick={() => removeVariant(active.id)}
                className="h-7 px-2 text-[10px] font-bold uppercase tracking-wider border border-[#E5E5E5] text-[#DC2626] hover:border-[#DC2626] flex items-center gap-1"
                data-testid={`wf-step-${stepIndex}-variant-${active.id}-remove`}>
          <Trash size={11} />{variants.length <= 2 ? "End A/B split" : `Remove variant ${active.id}`}
        </button>
      </div>
    </div>
  );
}


function StepPreviewModal({ step, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-6" onClick={onClose}>
      <div className="bg-white border border-[#E5E5E5] w-full max-w-3xl max-h-[90vh] flex flex-col"
           onClick={(e) => e.stopPropagation()} data-testid="wf-step-preview-modal">
        <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
              Step {step.idx + 1} · preview
            </p>
            <p className="text-sm font-bold text-[#0A0A0A] font-mono">{step.subject}</p>
            <p className="text-[11px] text-[#525252]">From: {step.from_name}</p>
          </div>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center hover:bg-[#F5F5F5]"
                  data-testid="wf-step-preview-close">
            <X size={14} />
          </button>
        </div>
        <iframe
          title="step-preview"
          /* XSS: srcDoc is sanitised before being parsed; sandbox="" also
             blocks script execution + same-origin cookies/storage access. */
          srcDoc={safeSrcDoc(step.body_html)}
          className="flex-1 w-full border-0 bg-white"
          sandbox=""
          data-testid="wf-step-preview-iframe"
        />
      </div>
    </div>
  );
}